// ============================================
// MEDFLOW - Services Page
// ============================================

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Package, Clock, DollarSign, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { servicesApi } from '@/lib/api';
import { isSupabaseAuthEnabled } from '@/lib/auth-config';
import { createSupabaseService, loadSupabaseServices, updateSupabaseService } from '@/lib/supabase-data';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

interface ProfessionalInfo {
  id: string;
  specialty: string;
  color: string;
  user: { name: string; email: string; phone?: string };
}

interface Service {
  id: string;
  name: string;
  description?: string;
  durationMinutes: number;
  price: number;
  color: string;
  isActive: boolean;
  professionals?: ProfessionalInfo[];
}

interface ServiceFormState {
  name: string;
  description: string;
  durationMinutes: string;
  price: string;
}

function ServiceModal({
  open,
  title,
  service,
  onClose,
  onSubmit,
  isLoading,
}: {
  open: boolean;
  title: string;
  service?: Service | null;
  onClose: () => void;
  onSubmit: (values: ServiceFormState) => void;
  isLoading: boolean;
}) {
  const [form, setForm] = useState<ServiceFormState>({
    name: '',
    description: '',
    durationMinutes: '30',
    price: '0',
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    queueMicrotask(() => {
      setForm({
        name: service?.name ?? '',
        description: service?.description ?? '',
        durationMinutes: service ? String(service.durationMinutes) : '30',
        price: service ? String(service.price ?? 0) : '0',
      });
    });
  }, [open, service]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="rounded-lg p-2 text-sm text-gray-500 hover:bg-gray-100">Cerrar</button>
        </div>
        <div className="space-y-4 px-6 py-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Input label="Nombre" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
            <Input label="Duración (min)" type="number" min="5" step="5" value={form.durationMinutes} onChange={(event) => setForm((current) => ({ ...current, durationMinutes: event.target.value }))} />
          </div>
          <Input label="Precio" type="number" min="0" step="1000" value={form.price} onChange={(event) => setForm((current) => ({ ...current, price: event.target.value }))} />
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Descripción</label>
            <textarea
              value={form.description}
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              rows={4}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={() => onSubmit(form)} loading={isLoading}>Guardar servicio</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ServicesPage() {
  const { clinic } = useAuthStore();
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['services', clinic?.id, isSupabaseAuthEnabled ? 'supabase' : 'legacy'],
    queryFn: async () => {
      if (isSupabaseAuthEnabled && clinic?.id) {
        return loadSupabaseServices(clinic.id);
      }

      const response = await servicesApi.list();
      return (response.data as Service[]) || [];
    },
    enabled: !isSupabaseAuthEnabled || Boolean(clinic?.id),
  });

  const services = data || [];

  const createMutation = useMutation({
    mutationFn: async (values: ServiceFormState) => {
      const payload = {
        name: values.name.trim(),
        description: values.description.trim() || undefined,
        durationMinutes: Number(values.durationMinutes),
        price: Number(values.price || 0),
      };

      if (!payload.name || payload.durationMinutes <= 0) {
        throw new Error('Nombre y duración válida son obligatorios.');
      }

      if (isSupabaseAuthEnabled) {
        if (!clinic?.id) {
          throw new Error('No hay una clínica activa.');
        }

        await createSupabaseService(clinic.id, payload);
        return;
      }

      await servicesApi.create(payload);
    },
    onSuccess: () => {
      setShowCreateModal(false);
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: ServiceFormState }) => {
      const payload = {
        name: values.name.trim(),
        description: values.description.trim() || undefined,
        durationMinutes: Number(values.durationMinutes),
        price: Number(values.price || 0),
      };

      if (!payload.name || payload.durationMinutes <= 0) {
        throw new Error('Nombre y duración válida son obligatorios.');
      }

      if (isSupabaseAuthEnabled) {
        await updateSupabaseService(id, payload);
        return;
      }

      await servicesApi.update(id, payload);
    },
    onSuccess: () => {
      setEditingService(null);
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async (id: string) => {
      if (isSupabaseAuthEnabled) {
        await updateSupabaseService(id, { isActive: false });
        return;
      }

      await servicesApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });

  const filteredServices = services.filter((service) => {
    const searchLower = search.toLowerCase();
    return (
      service.name.toLowerCase().includes(searchLower) ||
      service.description?.toLowerCase().includes(searchLower)
    );
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return minutes + ' min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? hours + 'h ' + mins + 'm' : hours + 'h';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Servicios</h1>
          <p className="text-gray-600">{services.length} servicios en el catalogo</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Agregar servicio
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar servicios..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full h-10 pl-10 pr-4 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {isLoading ? (
        <Card>
          <div className="h-64 bg-gray-100 animate-pulse" />
        </Card>
      ) : filteredServices.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="w-12 h-12 text-gray-300 mb-4" />
            <p className="text-gray-500 text-center">
              {search ? 'No se encontraron servicios' : 'No hay servicios registrados'}
            </p>
            <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Agregar servicio
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Servicio</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Profesionales</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Duracion</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Precio</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredServices.map((service) => (
                  <tr key={service.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: (service.color || '#6366f1') + '20' }}
                        >
                          <Package className="w-5 h-5" style={{ color: service.color || '#6366f1' }} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{service.name}</p>
                          {service.description && (
                            <p className="text-sm text-gray-500 truncate max-w-xs">{service.description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-wrap gap-1">
                        {service.professionals && service.professionals.length > 0 ? (
                          service.professionals.map((prof) => (
                            <div
                              key={prof.id}
                              className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium text-white"
                              style={{ backgroundColor: prof.color }}
                              title={prof.specialty}
                            >
                              {prof.user.name.split(' ').slice(0, 2).join(' ')}
                            </div>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">Sin asignar</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-1 text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{formatDuration(service.durationMinutes)}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-1 font-medium text-gray-900">
                        <DollarSign className="w-4 h-4 text-gray-400" />
                        <span>{formatPrice(service.price)}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <Badge className={cn(service.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700')}>
                        {service.isActive ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => setEditingService(service)}>
                          <Edit className="w-4 h-4 text-gray-400" />
                        </button>
                        <button
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          onClick={() => deactivateMutation.mutate(service.id)}
                          disabled={deactivateMutation.isPending}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{services.length}</p>
                <p className="text-sm text-gray-500">Total servicios</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{services.filter((s) => s.isActive).length}</p>
                <p className="text-sm text-gray-500">Servicios activos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {formatPrice(services.reduce((acc, s) => acc + (s.price || 0), 0) / Math.max(services.length, 1))}
                </p>
                <p className="text-sm text-gray-500">Precio promedio</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <ServiceModal
        open={showCreateModal}
        title="Nuevo servicio"
        onClose={() => setShowCreateModal(false)}
        onSubmit={(values) => createMutation.mutate(values)}
        isLoading={createMutation.isPending}
      />

      <ServiceModal
        open={Boolean(editingService)}
        title="Editar servicio"
        service={editingService}
        onClose={() => setEditingService(null)}
        onSubmit={(values) => {
          if (!editingService) {
            return;
          }

          updateMutation.mutate({ id: editingService.id, values });
        }}
        isLoading={updateMutation.isPending}
      />
    </div>
  );
}
